import React, { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Container, Paper, Typography } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import teamjakLogo from '../assets/images/teamjak-logo.png';

type DeviceType = 'ios' | 'android' | 'desktop';
type BrowserType = 'safari' | 'chrome' | 'edge' | 'firefox' | 'samsung' | 'other';

interface DeviceInfo {
  device: DeviceType;
  browser: BrowserType;
  iosVersion: number | null;
}

function detectDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent || '';

  // Detect device
  let device: DeviceType = 'desktop';
  if (/iPad|iPhone|iPod/.test(ua)) {
    device = 'ios';
  } else if (/android/i.test(ua)) {
    device = 'android';
  }

  // Detect iOS version
  let iosVersion: number | null = null;
  if (device === 'ios') {
    const match = ua.match(/OS (\d+)[_.]/);
    if (match) {
      iosVersion = parseInt(match[1], 10);
    }
  }

  // Detect browser
  let browser: BrowserType = 'other';
  if (device === 'ios') {
    // On iOS, check for specific browsers
    if (/CriOS/i.test(ua)) {
      browser = 'chrome';
    } else if (/EdgiOS/i.test(ua)) {
      browser = 'edge';
    } else if (/FxiOS/i.test(ua)) {
      browser = 'firefox';
    } else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
      browser = 'safari';
    }
  } else if (device === 'android') {
    if (/SamsungBrowser/i.test(ua)) {
      browser = 'samsung';
    } else if (/EdgA/i.test(ua)) {
      browser = 'edge';
    } else if (/Firefox/i.test(ua)) {
      browser = 'firefox';
    } else if (/Chrome/i.test(ua)) {
      browser = 'chrome';
    }
  }

  return { device, browser, iosVersion };
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

const InstallInstructions: React.FC<{ deviceInfo: DeviceInfo }> = ({ deviceInfo }) => {
  const { device, browser, iosVersion } = deviceInfo;

  if (device === 'desktop' || isStandalone()) return null;

  const renderIOSInstructions = () => {
    const isIOS26OrLater = iosVersion !== null && iosVersion >= 26;
    const isIOS17OrLater = iosVersion !== null && iosVersion >= 17;

    // iOS 26+ has a new "Open as Web App" toggle
    if (isIOS26OrLater) {
      return (
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' } }}>
          <li>
            Tap the <strong>Share</strong> button{' '}
            <IosShareIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />{' '}
            {browser === 'safari' ? 'at the bottom of your screen' : 'in the address bar'}
          </li>
          <li>
            Tap <strong>"Add to Home Screen"</strong>{' '}
            <AddBoxOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
          </li>
          <li>
            Keep <strong>"Open as Web App"</strong> enabled (it's on by default)
          </li>
          <li>
            Tap <strong>"Add"</strong> in the top right corner
          </li>
        </Box>
      );
    }

    // iOS 17+ Chrome/Edge have share button in URL bar
    if (isIOS17OrLater && (browser === 'chrome' || browser === 'edge')) {
      return (
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' } }}>
          <li>
            Tap the <strong>Share</strong> button{' '}
            <IosShareIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> in the address bar
          </li>
          <li>
            Tap <strong>"Add to Home Screen"</strong>{' '}
            <AddBoxOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
          </li>
          <li>
            Tap <strong>"Add"</strong> to confirm
          </li>
        </Box>
      );
    }

    // iOS 16.4+ Firefox
    if (browser === 'firefox' && iosVersion !== null && iosVersion >= 16) {
      return (
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' } }}>
          <li>
            Tap the <strong>menu</strong> button{' '}
            <MenuIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> (three lines)
          </li>
          <li>
            Tap <strong>"Share"</strong>, then <strong>"Add to Home Screen"</strong>
          </li>
          <li>
            Tap <strong>"Add"</strong> to confirm
          </li>
        </Box>
      );
    }

    // Default Safari instructions (works for all iOS versions)
    return (
      <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' } }}>
        <li>
          Tap the <strong>Share</strong> button{' '}
          <IosShareIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> at the bottom of your screen
        </li>
        <li>
          Scroll down and tap <strong>"Add to Home Screen"</strong>{' '}
          <AddBoxOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
        </li>
        <li>
          Tap <strong>"Add"</strong> in the top right corner
        </li>
      </Box>
    );
  };

  const renderAndroidInstructions = () => {
    // Samsung Internet Browser
    if (browser === 'samsung') {
      return (
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' } }}>
          <li>
            Look for the <strong>install icon</strong> (+ symbol) in the address bar, or
          </li>
          <li>
            Tap the <strong>menu</strong> button{' '}
            <MenuIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> (three lines)
          </li>
          <li>
            Tap <strong>"Add page to"</strong> → <strong>"Home screen"</strong>
          </li>
          <li>
            Tap <strong>"Add"</strong> to confirm
          </li>
        </Box>
      );
    }

    // Microsoft Edge
    if (browser === 'edge') {
      return (
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' } }}>
          <li>
            Tap the <strong>menu</strong> button{' '}
            <MoreHorizIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> (three dots) at the bottom
          </li>
          <li>
            Tap <strong>"Add to Phone"</strong>
          </li>
          <li>
            Tap <strong>"Add"</strong> to confirm
          </li>
        </Box>
      );
    }

    // Firefox
    if (browser === 'firefox') {
      return (
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' } }}>
          <li>
            Tap the <strong>menu</strong> button{' '}
            <MoreVertIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> (three dots)
          </li>
          <li>
            Tap <strong>"Install"</strong> or <strong>"Add to Home screen"</strong>
          </li>
          <li>
            Tap <strong>"Add"</strong> to confirm
          </li>
        </Box>
      );
    }

    // Default Chrome instructions (also works for Brave and others)
    return (
      <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' } }}>
        <li>
          Tap the <strong>menu</strong> button{' '}
          <MoreVertIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> (three dots) in the top right
        </li>
        <li>
          Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong>
        </li>
        <li>
          Tap <strong>"Add"</strong> or <strong>"Install"</strong> to confirm
        </li>
      </Box>
    );
  };

  const getBrowserName = () => {
    const browserNames: Record<BrowserType, string> = {
      safari: 'Safari',
      chrome: 'Chrome',
      edge: 'Edge',
      firefox: 'Firefox',
      samsung: 'Samsung Internet',
      other: 'your browser',
    };
    return browserNames[browser];
  };

  return (
    <Paper
      elevation={2}
      sx={{
        mt: 3,
        p: 3,
        borderRadius: 3,
        textAlign: 'left',
        backgroundColor: 'rgba(255,255,255,0.95)',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, textAlign: 'center' }}>
        Install Team Jak on your device
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, textAlign: 'center' }}>
        Using {getBrowserName()}
      </Typography>

      {device === 'ios' ? renderIOSInstructions() : renderAndroidInstructions()}
    </Paper>
  );
};

const AuthLayout: React.FC = () => {
  const deviceInfo = useMemo(() => detectDeviceInfo(), []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#00b7ed',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        {/* Logo above the card */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src={teamjakLogo}
            alt="Team Jak"
            sx={{
              height: { xs: 60, sm: 80 },
              width: 'auto',
            }}
          />
        </Box>

        <Paper
          elevation={6}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Event Planning Portal
            </Typography>
          </Box>
          <Outlet />
        </Paper>

        <InstallInstructions deviceInfo={deviceInfo} />
      </Container>
    </Box>
  );
};

export default AuthLayout;
